<template>
    <AdminLayout>
        <Head title="Erinnerungen Einstellungen" />

        <div class="py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm sm:rounded-lg">
                    <div class="p-6">
                        <div class="flex justify-between items-center mb-6">
                            <h2 class="text-2xl font-semibold">Erinnerungen Einstellungen</h2>
                            
                            <div v-if="isSuperAdmin" class="w-64">
                                <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Unternehmen</label>
                                <Dropdown
                                    v-model="selectedCompany"
                                    :options="companyOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Unternehmen wählen"
                                    @change="handleFilterChange"
                                    class="w-full !h-[42px] flex items-center"
                                    filter
                                />
                            </div>
                        </div>

                        <form @submit.prevent="submitForm" class="space-y-6">
                            <div>
                                <label for="title" class="block font-semibold mb-2">Titel:</label>
                                <InputText
                                    id="title"
                                    v-model="form.title"
                                    class="w-full"
                                    :class="{ 'p-invalid': form.errors.title }"
                                    placeholder="Geben Sie den Titel ein..."
                                    @input="form.clearErrors('title')"
                                    @blur="validateTitle"
                                />
                                <small class="p-error" v-if="form.errors.title">{{ form.errors.title }}</small>
                            </div>

                            <div>
                                <label for="body" class="block font-semibold mb-2">Nachricht:</label>
                                <Textarea
                                    id="body"
                                    v-model="form.body"
                                    rows="10"
                                    class="w-full"
                                    :class="{ 'p-invalid': form.errors.body }"
                                    placeholder="Geben Sie die Nachricht ein..."
                                    @input="form.clearErrors('body')"
                                    @blur="validateBody"
                                />
                                <small class="p-error" v-if="form.errors.body">{{ form.errors.body }}</small>
                            </div>

                            <!-- Variable Reference -->
                            <div class="bg-gray-50 p-4 rounded border border-gray-200">
                                <h3 class="font-semibold mb-3 text-gray-700">Verfügbare Variablen:</h3>
                                <div class="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span class="font-mono bg-white px-2 py-1 rounded border border-gray-300">{vorname}</span>
                                        <span class="text-gray-600 ml-2">Vorname</span>
                                    </div>
                                    <div>
                                        <span class="font-mono bg-white px-2 py-1 rounded border border-gray-300">{nachname}</span>
                                        <span class="text-gray-600 ml-2">Nachname</span>
                                    </div>
                                    <div>
                                        <span class="font-mono bg-white px-2 py-1 rounded border border-gray-300">{vollstaendiger_name}</span>
                                        <span class="text-gray-600 ml-2">Vollständiger Name</span>
                                    </div>
                                    <div>
                                        <span class="font-mono bg-white px-2 py-1 rounded border border-gray-300">{email}</span>
                                        <span class="text-gray-600 ml-2">E-Mail-Adresse</span>
                                    </div>
                                </div>
                                <p class="text-xs text-gray-500 mt-3">
                                    Diese Variablen werden beim Senden automatisch mit den Kundendaten ersetzt.
                                </p>
                            </div>

                            <div class="flex justify-end gap-2">
                                <Button
                                    label="Speichern"
                                    type="submit"
                                    icon="pi pi-save"
                                    :loading="form.processing"
                                    :disabled="form.processing"
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </AdminLayout>
</template>

<script setup>
import AdminLayout from '../../../../Layouts/AdminLayout.vue';
import { Head, useForm } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import { useToast } from 'primevue/usetoast';
import { ref, watch } from 'vue';

const toast = useToast();

const props = defineProps({
    settings: {
        type: Object,
        default: () => ({
            title: '',
            body: ''
        })
    },
    isSuperAdmin: {
        type: Boolean,
        default: false
    },
    companyOptions: {
        type: Array,
        default: () => []
    },
    filters: {
        type: Object,
        default: () => ({
            company_id: null
        })
    }
});

const selectedCompany = ref(props.filters.company_id);

const form = useForm({
    title: props.settings.title || '',
    body: props.settings.body || '',
    company_id: props.filters.company_id
});

// Update form values when props.settings change (after page reload with new company)
watch(() => props.settings, (newSettings) => {
    form.title = newSettings.title || '';
    form.body = newSettings.body || '';
}, { deep: true });

// Update company_id when selectedCompany changes
watch(selectedCompany, (newVal) => {
    form.company_id = newVal;
});

const validateTitle = () => {
    if (!form.title || form.title.trim() === '') {
        form.setError('title', 'Titel ist erforderlich');
    }
};

const validateBody = () => {
    if (!form.body || form.body.trim() === '') {
        form.setError('body', 'Nachricht ist erforderlich');
    }
};

const handleFilterChange = () => {
    const params = {};

    if (props.isSuperAdmin) {
        params.company_id = selectedCompany.value;
    }

    Inertia.get('/admin/alerts/settings', params, {
        preserveScroll: true
    });
};

const submitForm = () => {
    // Frontend validation
    form.clearErrors();

    let hasErrors = false;

    if (!form.title || form.title.trim() === '') {
        form.setError('title', 'Titel ist erforderlich');
        hasErrors = true;
    }

    if (!form.body || form.body.trim() === '') {
        form.setError('body', 'Nachricht ist erforderlich');
        hasErrors = true;
    }

    if (hasErrors) {
        return;
    }

    form.post('/admin/alerts/settings', {
        preserveScroll: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Einstellungen erfolgreich aktualisiert',
                life: 5000,
            });
        },
    });
};
</script>
